import {
  Outlet,
  createRootRoute,
  HeadContent,
  retainSearchParams,
  useNavigate,
  useLocation,
} from '@tanstack/react-router';
import 'ui_2/src/index.css';
import { useArcadeStoreState } from '@src/hooks/useArcadeStoreState';
import { useEffect } from 'react';
import { z } from 'zod';
import { connect, getIsConnected, useOnDisconnect } from '@puzzlehq/sdk';
import { useAuth } from '@src/data/settings';

const addressSchema = z.object({
  address: z.string().optional(),
  joinEventIDs: z.string().array().optional(),
  auth: z.string().optional(),
  challengeAuth: z.string().optional(),
});

export const Route = createRootRoute({
  validateSearch: addressSchema,
  search: {
    middlewares: [
      retainSearchParams(['address', 'joinEventIDs', 'auth', 'challengeAuth']),
    ],
  },
  head: () => ({
    meta: [
      {
        name: 'description',
        content:
          "Transform your Puzzle Arcade $PART winnings into gift cards with Aleo's Zero Knowledge (ZK) technology. A seamless and fun way to spend your rewards at popular retailers.",
      },
      {
        title: 'Puzzle Arcade Store',
      },
    ],
  }),
  beforeLoad: async () => {
    const isPuzzleWalletWebview =
      window.navigator.userAgent?.includes('PuzzleWallet');
    if (isPuzzleWalletWebview === false) return;

    try {
      const isConnected = await getIsConnected();
      if (isConnected) return;
      const connectResponse = await connect({
        permissions: {
          programIds: {
            [import.meta.env.VITE_NETWORK!]: ['ALL_PROGRAM_IDS'],
          },
        },
        dAppInfo: {
          name: 'Puzzle Arcade Store',
        },
      });
      console.log(`/ connectResponse ${JSON.stringify(connectResponse)}`);
    } catch (e) {
      console.error(`Error calling connect on app startup`, e);
    }
  },
  component: function Component() {
    const navigate = useNavigate({ from: Route.fullPath });
    const { account } = useArcadeStoreState();

    const { pathname } = useLocation();

    useEffect(() => {
      if (account?.address) {
        navigate({
          to: pathname,
          search: (prev) => ({ ...prev, address: account.address }),
        });
      }
    }, [account?.address, navigate]);

    useOnDisconnect(() => {
      navigate({
        to: pathname,
        search: (prev) => ({ ...prev, address: undefined }),
      });
    }, []);

    const { auth, challengeAuth } = Route.useSearch();
    const [_auth, setAuth, _challengeAuth, setChallengeAuth] = useAuth(
      (state) => [
        state.auth,
        state.setAuth,
        state.challengeAuth,
        state.setChallengeAuth,
      ]
    );

    useEffect(() => {
      console.log(`auth: ${auth}`);
      console.log(`challengeAuth: ${challengeAuth}`);
      if (challengeAuth && challengeAuth !== _challengeAuth) {
        setChallengeAuth(challengeAuth);
        navigate({
          to: pathname,
          search: (prev) => ({ ...prev, challengeAuth: undefined }),
        });
      } else if (auth && auth !== _auth) {
        setAuth(auth);
        navigate({
          to: pathname,
          search: (prev) => ({ ...prev, auth: undefined }),
        });
      }
    }, [auth, challengeAuth]);

    useEffect(() => {
      console.log(`_auth: ${_auth}`);
      console.log(`_challengeAuth: ${_challengeAuth}`);
    }, [_auth, _challengeAuth]);

    return (
      <>
        <HeadContent />
        <div className='h-[100dvh] w-full'>
          <Outlet />
        </div>
      </>
    );
  },
});
